/**
 * Game media carousel + age gate (partials/game-media.php).
 *
 * The page is served from a full-page cache, so the markup is identical for every
 * visitor and the gate has to be resolved here rather than in PHP. Two rules follow, and
 * both are load-bearing:
 *
 *   1. The covered state is what PHP ships. This file only ever *removes* the cover.
 *      If this script fails to run, a gated section stays covered - the safe direction.
 *   2. Gated assets have no real `src` until the visitor confirms. Revealing means
 *      copying data-age-gate-src into src; until then nothing is fetched over the network.
 *
 * Slick is initialised only once the slides have actually loaded, because variableWidth
 * measures each slide and unloaded images measure zero.
 */

interface EsrbModalGlobal {
	isSupported(): boolean;
	open( target: string | HTMLDialogElement, trigger?: HTMLElement | null ): boolean;
	close( dialog?: HTMLDialogElement | null ): void;
	create( content: Node, variant?: string ): HTMLDialogElement;
	closeButton(): HTMLButtonElement;
}

(function ( $ ) {
	'use strict';

	// Fallback only. The real name carries an editable key (data-gate-cookie), so bumping
	// it in Theme Settings renames the cookie and invalidates every existing confirmation.
	var COOKIE_FALLBACK = 'esrb_age_ok_v1';

	function cookieName( section: HTMLElement ): string {
		return section.getAttribute( 'data-gate-cookie' ) || COOKIE_FALLBACK;
	}

	function engine(): EsrbModalGlobal | null {
		var api = ( window as unknown as { EsrbModal?: EsrbModalGlobal } ).EsrbModal;

		return api && api.isSupported() ? api : null;
	}

	/**
	 * Alt text of the thumbnail inside a slide, reused as the lightbox label.
	 *
	 * Written without optional chaining on purpose: gulp-uglify-es in this toolchain
	 * cannot parse `?.`, and the other TypeScript sources avoid it for the same reason.
	 */
	function altOf( el: Element ): string {
		var image = el.querySelector( 'img' );

		return image ? ( image.getAttribute( 'alt' ) || '' ) : '';
	}

	/** Detach a node if it is still attached. */
	function remove( node: HTMLElement ): void {
		if ( node.parentNode ) {
			node.parentNode.removeChild( node );
		}
	}

	/* ------------------------------------------------------------------------ cookie ---- */

	// Written into the cookie when the date entered is below the threshold.
	//
	// A number rather than a word, so the cookie reads as a number whichever way it went
	// and a glance through devtools does not announce which visitors have been refused.
	// Obscurity, not security - a client-side gate is editable by anyone who wants to edit
	// it, and the guidance asks only for reasonable measures.
	//
	// It is negative so that it also fails the plain numeric test: the threshold is always
	// at least 1, so any code path that compares without checking for this value treats the
	// visitor as unverified and leaves the gate up, which is the safe way to be wrong.
	var BLOCKED = '-1';

	/**
	 * The threshold a visitor has to clear here, which is also what gets stored on success.
	 *
	 * The cookie holds the threshold that was satisfied, never the birthdate that was typed
	 * or the age derived from it: it carries nothing personal, and raising the threshold in
	 * Theme Settings invalidates every confirmation granted under the old one by itself.
	 */
	function requiredAge( section: HTMLElement ): number {
		return parseInt( section.getAttribute( 'data-gate-min-age' ) || '17', 10 ) || 17;
	}

	/** Raw cookie value, or '' when it is absent or unreadable. */
	function gateCookie( name: string ): string {
		try {
			var found = '';

			document.cookie.split( ';' ).forEach( function ( part ) {
				var pair = part.trim();

				if ( pair.indexOf( name + '=' ) === 0 ) {
					found = pair.slice( name.length + 1 );
				}
			} );

			return found;
		} catch ( error ) {
			// Cookies can throw in hardened privacy modes; treat as "nothing stored" so the
			// gate stays up rather than failing open.
			return '';
		}
	}

	/**
	 * One cookie carries both outcomes, under one name and one lifetime, so the consent
	 * tooling has a single entry to classify. The value is what differs: the threshold that
	 * was cleared, or BLOCKED.
	 *
	 * The guidance calls for a session cookie on failure. This expires on the configured
	 * TTL instead - a deliberate trade for that single uniform entry - which still stops a
	 * refused visitor retrying with a different date for as long as the setting says.
	 */
	function remember( name: string, minutes: number, value: string ): void {
		var expires = new Date( Date.now() + minutes * 60 * 1000 ).toUTCString();
		var secure = 'https:' === window.location.protocol ? ';Secure' : '';

		try {
			document.cookie = name + '=' + value + ';expires=' + expires + ';path=/;SameSite=Lax' + secure;
		} catch ( error ) {
			// Non-fatal: the visitor simply gets asked again on the next gated title.
		}
	}

	function ttlMinutes( section: HTMLElement ): number {
		return parseInt( section.getAttribute( 'data-gate-ttl' ) || '60', 10 ) || 60;
	}

	/* ------------------------------------------------------------------- date of birth --- */

	/**
	 * Age in whole years on a birthdate, or -1 when the three fields are not a real date.
	 *
	 * Three numbers in range are not yet a date - 02/31/2000 passes every individual check -
	 * so the parts are round-tripped through Date and compared back. A date in the future is
	 * rejected the same way as nonsense, because it is nonsense.
	 */
	function ageFromParts( month: string, day: string, year: string ): number {
		if ( !/^[0-9]{1,2}$/.test( month ) || !/^[0-9]{1,2}$/.test( day ) || !/^[0-9]{4}$/.test( year ) ) {
			return -1;
		}

		var m = parseInt( month, 10 );
		var d = parseInt( day, 10 );
		var y = parseInt( year, 10 );

		if ( m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 ) {
			return -1;
		}

		var born = new Date( y, m - 1, d );

		// Date rolls an impossible day into the next month; if it did, this was not a date.
		if ( born.getFullYear() !== y || born.getMonth() !== m - 1 || born.getDate() !== d ) {
			return -1;
		}

		var today = new Date();

		if ( born > today ) {
			return -1;
		}

		var age = today.getFullYear() - y;
		var monthsOff = today.getMonth() - ( m - 1 );

		// Birthday not reached yet this year.
		if ( monthsOff < 0 || ( 0 === monthsOff && today.getDate() < d ) ) {
			age--;
		}

		return age;
	}

	/* ----------------------------------------------------------------------- lightbox --- */

	/**
	 * The lightbox is a slider over the whole media set, not a viewer for one item.
	 * Opening a slide and then having to close it just to see the next one is the thing
	 * this avoids, so the dialog carries its own arrows, counter and keyboard handling.
	 *
	 * Only one video may be playing at a time: moving off a slide tears its iframe back
	 * down to the poster, which both stops the audio and keeps a long gallery cheap.
	 */

	interface MediaItem {
		type: 'image' | 'video';
		full: string;
		thumb: string;
		alt: string;
	}

	/**
	 * Read the media set out of the rendered carousel.
	 *
	 * Taken from the DOM rather than a separate JSON payload so there is exactly one
	 * source of truth in the markup. Slick clones slides when looping; `infinite` is off
	 * here so it should not, but cloned nodes are filtered anyway - a duplicate would
	 * otherwise show up twice in the gallery and throw the counter out.
	 */
	function collectItems( section: HTMLElement ): MediaItem[] {
		var items: MediaItem[] = [];

		Array.prototype.forEach.call(
			section.querySelectorAll( '.game-media__slide' ),
			function ( slide: HTMLElement ) {
				if ( slide.classList.contains( 'slick-cloned' ) ) {
					return;
				}

				var video = slide.getAttribute( 'data-media-video' );
				var full = slide.getAttribute( 'data-media-full' );
				var image = slide.querySelector( 'img' );

				if ( !video && !full ) {
					return;
				}

				items.push( {
					type: video ? 'video' : 'image',
					full: ( video || full ) as string,
					thumb: image ? image.src : '',
					alt: altOf( slide )
				} );
			}
		);

		return items;
	}

	/** Index of a slide within the set collectItems() returns. */
	function indexOf( section: HTMLElement, slide: HTMLElement ): number {
		var all = Array.prototype.filter.call(
			section.querySelectorAll( '.game-media__slide' ),
			function ( node: HTMLElement ) {
				return !node.classList.contains( 'slick-cloned' );
			}
		);

		return Array.prototype.indexOf.call( all, slide );
	}

	function youtubeFrame( src: string, title: string ): HTMLIFrameElement {
		var frame = document.createElement( 'iframe' );

		// Nothing here enables YouTube's JS API. Controlling playback that way needs
		// `enablejsapi=1`, which pulls in youtube.com alongside the nocookie host and adds
		// storage the consent tooling has to account for. Pausing is done by removing the
		// player instead - see deactivateVideo() - which needs no API and leaves nothing
		// behind.
		frame.src = src + ( src.indexOf( '?' ) === -1 ? '?' : '&' ) + 'rel=0&autoplay=1';
		frame.setAttribute( 'frameborder', '0' );
		frame.setAttribute( 'allow', 'autoplay; encrypted-media' );
		frame.setAttribute( 'allowfullscreen', 'true' );
		frame.setAttribute( 'title', title );

		return frame;
	}

	function buildSlide( item: MediaItem ): HTMLElement {
		var slide = document.createElement( 'div' );

		slide.className = 'media-lightbox__slide media-lightbox__slide--' + item.type;

		if ( 'image' === item.type ) {
			var image = document.createElement( 'img' );

			image.className = 'media-lightbox__image';
			image.src = item.full;
			image.alt = item.alt;
			slide.appendChild( image );

			return slide;
		}

		// Video slides start as a poster with a play control; the iframe is only
		// created on activation, so opening the gallery never autoplays six trailers.
		slide.setAttribute( 'data-embed', item.full );

		var button = document.createElement( 'button' );

		button.type = 'button';
		button.className = 'media-lightbox__play-trigger';
		button.setAttribute( 'aria-label', item.alt );

		if ( item.thumb ) {
			var poster = document.createElement( 'img' );

			poster.className = 'media-lightbox__poster';
			poster.src = item.thumb;
			poster.alt = '';
			button.appendChild( poster );
		}

		var play = document.createElement( 'span' );

		play.className = 'game-media__play';
		play.setAttribute( 'aria-hidden', 'true' );
		button.appendChild( play );
		slide.appendChild( button );

		return slide;
	}

	/** Swap a video slide's poster for a playing iframe. */
	function activateVideo( slide: HTMLElement ): void {
		if ( slide.querySelector( 'iframe' ) ) {
			return;
		}

		var embed = slide.getAttribute( 'data-embed' );

		if ( !embed ) {
			return;
		}

		var label = '';
		var trigger = slide.querySelector( '.media-lightbox__play-trigger' );

		if ( trigger ) {
			label = trigger.getAttribute( 'aria-label' ) || '';
		}

		slide.innerHTML = '';
		slide.appendChild( youtubeFrame( embed, label ) );
	}

	/** Put a video slide back to its poster, which also stops playback. */
	function deactivateVideo( slide: HTMLElement, item: MediaItem ): void {
		if ( !slide.querySelector( 'iframe' ) ) {
			return;
		}

		var rebuilt = buildSlide( item );

		slide.innerHTML = rebuilt.innerHTML;
	}

	function openLightbox( section: HTMLElement, startIndex: number, trigger: HTMLElement ): void {
		var api = engine();

		if ( !api ) {
			return;
		}

		var items = collectItems( section );

		if ( !items.length ) {
			return;
		}

		var index = startIndex >= 0 && startIndex < items.length ? startIndex : 0;

		var box = document.createElement( 'div' );

		box.className = 'media-lightbox';

		var track = document.createElement( 'div' );

		track.className = 'media-lightbox__slides';
		items.forEach( function ( item ) {
			track.appendChild( buildSlide( item ) );
		} );

		var arrows = document.createElement( 'div' );

		arrows.className = 'media-lightbox__arrows';

		var counter = document.createElement( 'p' );

		counter.className = 'media-lightbox__counter';
		counter.setAttribute( 'aria-live', 'polite' );

		box.appendChild( track );
		box.appendChild( arrows );
		box.appendChild( counter );

		var frag = document.createDocumentFragment();

		frag.appendChild( box );
		frag.appendChild( api.closeButton() );

		var dialog = api.create( frag, 'modal--gallery' );

		// Open first: Slick measures its slides, and a dialog that has not been shown
		// yet has no layout to measure.
		api.open( dialog, trigger );

		var $track = $( track );

		function label( i: number ): void {
			counter.textContent = ( i + 1 ) + ' / ' + items.length;
		}

		label( index );

		( $track as any ).slick( {
			initialSlide: index,
			infinite: false,
			slidesToShow: 1,
			slidesToScroll: 1,
			arrows: items.length > 1,
			dots: false,
			adaptiveHeight: false,
			appendArrows: $( arrows ),
			// Slick's own key handling, so Left/Right work once focus is in the dialog.
			accessibility: true
		} );

		( $track as any ).on( 'beforeChange', function ( _e: unknown, _slick: unknown, current: number ) {
			// Suppress the play control for the whole move. Slick marks the INCOMING
			// slide active as the transition starts, so keying off .slick-active alone
			// still lets that slide's orange circle be sliced by .slick-list's overflow
			// on its way in - which is the thin orange line that travels with it.
			box.classList.add( 'is-sliding' );

			var slides = track.querySelectorAll<HTMLElement>( '.media-lightbox__slide:not(.slick-cloned)' );

			if ( slides[ current ] && 'video' === items[ current ].type ) {
				deactivateVideo( slides[ current ], items[ current ] );
			}
		} );

		( $track as any ).on( 'afterChange', function ( _e: unknown, _slick: unknown, current: number ) {
			box.classList.remove( 'is-sliding' );
			label( current );
		} );

		// If the visitor clicked a trailer, start it rather than making them click again.
		if ( 'video' === items[ index ].type ) {
			var opening = track.querySelectorAll<HTMLElement>( '.media-lightbox__slide:not(.slick-cloned)' )[ index ];

			if ( opening ) {
				activateVideo( opening );
			}
		}

		// Play controls inside the gallery.
		box.addEventListener( 'click', function ( event: MouseEvent ) {
			var origin = event.target;

			if ( !( origin instanceof Element ) ) {
				return;
			}

			var play = origin.closest<HTMLElement>( '.media-lightbox__play-trigger' );

			if ( !play ) {
				return;
			}

			var slide = play.closest<HTMLElement>( '.media-lightbox__slide' );

			if ( slide ) {
				event.preventDefault();
				activateVideo( slide );
			}
		} );

		// Slick's accessibility handling only fires when one of its own nodes has focus;
		// after closing a video the focus may be anywhere in the dialog.
		dialog.addEventListener( 'keydown', function ( event: KeyboardEvent ) {
			var origin = event.target;

			if ( 'ArrowLeft' === event.key ) {
				( $track as any ).slick( 'slickPrev' );

				return;
			}

			if ( 'ArrowRight' === event.key ) {
				( $track as any ).slick( 'slickNext' );

				return;
			}

			if ( ' ' !== event.key && 'Spacebar' !== event.key && 'Enter' !== event.key ) {
				return;
			}

			// A real control handles its own Space/Enter - do not hijack the close button
			// or the arrows, and do not double-fire on the play button itself.
			if ( origin instanceof Element && origin.closest( 'button, a[href], input, select, textarea' ) ) {
				return;
			}

			// Otherwise the dialog is showing a trailer and focus is somewhere neutral
			// (the panel, where the modal engine puts it on open). Space or Enter should
			// start playback rather than doing nothing.
			var current = track.querySelector<HTMLElement>( '.media-lightbox__slide.slick-active' );

			if ( !current ) {
				return;
			}

			// Space would otherwise scroll the page behind the dialog.
			event.preventDefault();

			if ( current.querySelector( 'iframe' ) ) {
				// Stopping means removing the player and putting the poster back, the same
				// teardown used when navigating away. No player API, nothing left running.
				var slides = track.querySelectorAll<HTMLElement>( '.media-lightbox__slide:not(.slick-cloned)' );
				var at = Array.prototype.indexOf.call( slides, current );

				if ( at >= 0 && items[ at ] ) {
					deactivateVideo( current, items[ at ] );
				}

				return;
			}

			if ( current.querySelector( '.media-lightbox__play-trigger' ) ) {
				activateVideo( current );
			}
		} );

		// Tear the slider down with the dialog, so its handlers do not outlive it.
		dialog.addEventListener( 'close', function () {
			try {
				( $track as any ).slick( 'unslick' );
			} catch ( error ) {
				// Already gone; the dialog removes itself either way.
			}
		} );
	}

	/* ----------------------------------------------------------------------- carousel --- */

	/**
	 * Undo Slick's tabindex/aria-hidden bookkeeping on the page carousel.
	 *
	 * Slick assumes one slide is shown at a time, so it marks every non-current slide
	 * `tabindex="-1" aria-hidden="true"`. This carousel runs variableWidth with several
	 * slides visible at once, which makes that assumption wrong twice over: visible
	 * images end up hidden from assistive tech, and a keyboard user can only ever open
	 * the lightbox for whichever slide happens to be current - every other image is
	 * unreachable without first arrowing it into place.
	 *
	 * Slick re-applies its own attributes on every setSlideClasses(), so this has to run
	 * again after each change rather than once at init.
	 *
	 * The lightbox deliberately keeps Slick's behaviour: there, one slide at a time is
	 * genuinely all there is.
	 */
	function openSlidesToKeyboard( section: HTMLElement ): void {
		Array.prototype.forEach.call(
			section.querySelectorAll( '.game-media__slide' ),
			function ( slide: HTMLElement ) {
				if ( slide.classList.contains( 'slick-cloned' ) ) {
					return;
				}

				slide.setAttribute( 'tabindex', '0' );
				slide.removeAttribute( 'aria-hidden' );
			}
		);
	}

	function initSlider( section: HTMLElement ): void {
		var $slides = $( '.game-media__slides', section );

		if ( !$slides.length || $slides.hasClass( 'slick-initialized' ) ) {
			return;
		}

		// Wait for the images, but never wait forever.
		//
		// variableWidth measures every slide, and an image that has not decoded yet
		// measures zero and collapses the track - hence imagesLoaded rather than DOM
		// ready. But the slides carry loading="lazy" and several sit outside the viewport
		// horizontally, so the browser may defer them indefinitely; imagesLoaded would
		// then never fire and the slides, hidden by CSS until it does, would never
		// appear at all. The timeout guarantees the carousel shows up regardless, and
		// setPosition() below re-measures each straggler as it arrives.
		var started = false;

		function start(): void {
			if ( started ) {
				return;
			}

			started = true;

			// Same signal drives the assemble animation: CSS holds the slides hidden
			// behind a shimmer until this class lands, then staggers them in. Added
			// before slick() so the first painted frame is already the animated one.
			$slides.addClass( 'is-ready' );

			( $slides as any ).slick( {
				variableWidth: true,
				infinite: false,
				slidesToShow: 1,
				slidesToScroll: 1,
				arrows: true,
				dots: false,
				// Slick's ADA handling assumes one slide on screen at a time and keeps
				// rewriting tabindex="-1" onto every non-current slide - including, here,
				// the two or three that are plainly visible. Re-applying our own values
				// after the fact loses the race, because Slick rewrites them synchronously
				// inside setSlideClasses() on the way into a move. Switching it off is the
				// only way to own tabindex; the arrow keys it also provides are replaced
				// below, and aria-hidden is handled in openSlidesToKeyboard() because
				// setSlideClasses() applies that one regardless of this flag.
				accessibility: false,
				appendArrows: $( '.game-media__arrows', section ),
				appendDots: $( '.game-media__controls .dots', section ),
				responsive: [
					{
						breakpoint: 768,
						// variableWidth off below this width so Slick sizes each slide to
						// the viewport instead of to its image; the CSS then crops the
						// image to fill. See the media-breakpoint-down(sm) block in
						// game-media.scss.
						settings: { dots: true, arrows: false, variableWidth: false }
					}
				]
			} );

			openSlidesToKeyboard( section );

			( $slides as any ).on( 'afterChange setPosition breakpoint', function () {
				openSlidesToKeyboard( section );
			} );

			// Left/Right across the carousel, replacing what accessibility:false removed.
			section.addEventListener( 'keydown', function ( event: KeyboardEvent ) {
				if ( event.metaKey || event.ctrlKey || event.altKey || event.shiftKey ) {
					return;
				}

				var origin = event.target;

				if ( !( origin instanceof Element ) || !origin.closest( '.game-media__slides' ) ) {
					return;
				}

				if ( 'ArrowLeft' !== event.key && 'ArrowRight' !== event.key ) {
					return;
				}

				event.preventDefault();

				try {
					( $slides as any ).slick( 'ArrowLeft' === event.key ? 'slickPrev' : 'slickNext' );
				} catch ( error ) {
					// Slider torn down.
				}
			} );

			// Tabbing to a slide that is clipped out of view makes the browser scroll
			// .slick-list sideways to reveal it, which desyncs Slick's transform and
			// leaves the track visibly offset. Put the scroll back and move the carousel
			// properly instead. Keyboard focus only - :focus-visible is false for a mouse
			// click, which already has its own handling.
			section.addEventListener( 'focusin', function ( event: FocusEvent ) {
				var origin = event.target;

				if ( !( origin instanceof Element ) ) {
					return;
				}

				var slide = origin.closest<HTMLElement>( '.game-media__slide' );

				if ( !slide ) {
					return;
				}

				var list = section.querySelector( '.slick-list' );

				if ( list && list.scrollLeft !== 0 ) {
					list.scrollLeft = 0;
				}

				if ( !slide.matches( ':focus-visible' ) ) {
					return;
				}

				var index = parseInt( slide.getAttribute( 'data-slick-index' ) || '-1', 10 );

				if ( index >= 0 ) {
					try {
						( $slides as any ).slick( 'slickGoTo', index );
					} catch ( error ) {
						// Slider torn down; nothing to move.
					}
				}
			} );

			// Same clipping problem as the lightbox: a play control on a slide that is
			// moving through the overflow edge renders as an orange sliver.
			( $slides as any ).on( 'beforeChange', function () {
				$slides.addClass( 'is-sliding' );
			} );

			( $slides as any ).on( 'afterChange', function () {
				$slides.removeClass( 'is-sliding' );
			} );

			// Any image that had not decoded when we started would have been measured as
			// zero-width; re-measure the track as each one arrives.
			Array.prototype.forEach.call(
				section.querySelectorAll( 'img' ),
				function ( image: HTMLImageElement ) {
					if ( image.complete ) {
						return;
					}

					image.addEventListener( 'load', function () {
						try {
							( $slides as any ).slick( 'setPosition' );
						} catch ( error ) {
							// Slider already torn down; nothing to re-measure.
						}
					} );
				}
			);
		}

		try {
			( $slides as any ).imagesLoaded( start );
		} catch ( error ) {
			// Plugin missing or broken; the timeout below is already the fallback for it
			// never firing, so the carousel still appears.
		}

		window.setTimeout( start, 2500 );
	}

	/* --------------------------------------------------------------------------- gate --- */

	/**
	 * Swap withheld URLs into place. Nothing is fetched before this runs.
	 *
	 * The attribute is `data-age-gate-src`, deliberately NOT the conventional `data-src`:
	 * Smush's lazy loader is active on this site and hydrates `data-src` into `src` by
	 * itself, which fetched the gated imagery before the visitor had confirmed anything.
	 * This is age-gate-specific deferral, not general lazy loading, and the name says so -
	 * which also keeps it clear of any lazy loader that may be enabled later.
	 */
	function loadAssets( section: HTMLElement ): void {
		Array.prototype.forEach.call(
			section.querySelectorAll( 'img[data-age-gate-src]' ),
			function ( image: HTMLImageElement ) {
				var src = image.getAttribute( 'data-age-gate-src' );

				if ( src ) {
					image.src = src;
					image.removeAttribute( 'data-age-gate-src' );
				}
			}
		);
	}

	function reveal( section: HTMLElement, store: boolean ): void {
		if ( store ) {
			remember( cookieName( section ), ttlMinutes( section ), String( requiredAge( section ) ) );
		}

		var gate = section.querySelector<HTMLElement>( '.game-media__gate' );
		var slides = section.querySelector<HTMLElement>( '.game-media__slides' );

		section.classList.remove( 'game-media--gated' );

		// Hand the media back to the keyboard and to assistive tech. PHP renders both of
		// these plus tabindex="-1" on each slide while covered; initSlider() below restores
		// the slides' own tabindex through openSlidesToKeyboard().
		if ( slides ) {
			slides.removeAttribute( 'inert' );
			slides.removeAttribute( 'aria-hidden' );
		}

		loadAssets( section );

		if ( gate ) {
			remove( gate );
		}

		initSlider( section );
	}

	/**
	 * Refused: swap the panel over to the message and leave everything else as it is. The
	 * media keeps its blur and its withheld URLs, so nothing is fetched.
	 *
	 * The refusal IS remembered, unlike every earlier version of this gate. The guidance the
	 * client supplied requires it - a visitor who fails must not be able to retry with a
	 * different birthdate - which is why the cookie is written here and read back on load.
	 * `store` is false on that read-back, so restoring the state does not re-stamp the TTL
	 * and extend the block indefinitely across page views.
	 */
	function block( section: HTMLElement, store: boolean ): void {
		var panel = section.querySelector<HTMLElement>( '.game-media__gate-panel' );

		if ( !panel ) {
			return;
		}

		if ( store ) {
			remember( cookieName( section ), ttlMinutes( section ), BLOCKED );
		}

		// Hides the title, body and form in CSS rather than here, so the panel has one
		// state to style instead of a scattering of inline display values.
		panel.classList.add( 'is-blocked' );

		var fail = panel.querySelector<HTMLElement>( '[data-gate-fail]' );

		if ( !fail ) {
			return;
		}

		fail.hidden = false;

		// Only when the refusal just happened. Then the focus is what moves a screen reader
		// to the message, and preventScroll keeps the page where the visitor left it. On a
		// restore this runs during load, where moving focus into the middle of the page
		// unprompted is a focus steal - and the message is read in document order anyway.
		if ( store ) {
			fail.focus( { preventScroll: true } );
		}
	}

	/* ------------------------------------------------------------------------- wiring --- */

	$( function () {
		Array.prototype.forEach.call(
			document.querySelectorAll<HTMLElement>( '.game-media' ),
			function ( section: HTMLElement ) {
				if ( !section.classList.contains( 'game-media--gated' ) ) {
					initSlider( section );

					return;
				}

				// Restore whichever outcome this visitor already reached. Neither branch
				// re-stamps the cookie, so a stored decision expires when it was going to
				// rather than being pushed forward by every page view.
				var stored = gateCookie( cookieName( section ) );

				try {
					if ( BLOCKED === stored ) {
						block( section, false );
					} else if ( ( parseInt( stored, 10 ) || 0 ) >= requiredAge( section ) ) {
						reveal( section, false );
					}
				} finally {
					// In a finally because the cloak coming off is not conditional on the
					// branch above succeeding. Left on by a throw, the section renders as an
					// empty strip with neither gate nor media; taken off, the worst case is
					// the gate showing to someone already verified, who can simply answer
					// again. Until this runs the section shows only its skeleton, which is
					// what stops a verified visitor seeing the form flash.
					section.classList.remove( 'game-media--pending' );
				}
			}
		);
	} );

	/**
	 * Date of birth. Three outcomes: not a real date (ask again), a real date below the
	 * threshold (refuse and remember), at or above it (reveal and remember).
	 *
	 * Delegated on `submit` so Enter in the field works the same as the button, and so a
	 * gate that arrives after load is still wired up.
	 */
	document.addEventListener( 'submit', function ( event: Event ) {
		var form = event.target;

		if ( !( form instanceof HTMLElement ) || !form.matches( '[data-gate-form]' ) ) {
			return;
		}

		// Nothing is ever posted: the comparison happens here.
		event.preventDefault();

		var section = form.closest<HTMLElement>( '.game-media' );

		if ( !section ) {
			return;
		}

		var error = form.querySelector<HTMLElement>( '[data-gate-error]' );
		var parts = form.querySelectorAll<HTMLInputElement>( '[data-gate-dob]' );
		var typed: { [ key: string ]: string } = {};

		Array.prototype.forEach.call( parts, function ( field: HTMLInputElement ) {
			typed[ field.getAttribute( 'data-gate-dob' ) || '' ] = field.value.trim();
		} );

		var age = ageFromParts( typed.month || '', typed.day || '', typed.year || '' );

		function mark( invalid: boolean ): void {
			Array.prototype.forEach.call( parts, function ( field: HTMLInputElement ) {
				if ( invalid ) {
					field.setAttribute( 'aria-invalid', 'true' );
				} else {
					field.removeAttribute( 'aria-invalid' );
				}
			} );
		}

		// Not a date at all. This is a typo, not a failed check, so nothing is remembered
		// and the visitor can correct it - only a real date below the threshold is a failure.
		if ( age < 0 ) {
			if ( error ) {
				error.hidden = false;
			}

			mark( true );

			if ( parts.length ) {
				parts[ 0 ].focus();
			}

			return;
		}

		if ( error ) {
			error.hidden = true;
		}

		mark( false );

		if ( age < requiredAge( section ) ) {
			block( section, true );

			return;
		}

		reveal( section, true );
	} );

	/**
	 * Move to the next box once one is full, and back on backspace from an empty one.
	 *
	 * Three separate inputs are what the guidance asks for, but they are only pleasant to
	 * type into if they behave like one field.
	 */
	document.addEventListener( 'input', function ( event: Event ) {
		var field = event.target;

		if ( !( field instanceof HTMLInputElement ) || !field.matches( '[data-gate-dob]' ) ) {
			return;
		}

		// Phone keypads and pasted text can both deliver non-digits whatever the input mode
		// says, and a stray character would fail a check the visitor cannot see the cause of.
		field.value = field.value.replace( /[^0-9]/g, '' );

		if ( field.value.length < field.maxLength ) {
			return;
		}

		var form = field.closest( '[data-gate-form]' );

		if ( !form ) {
			return;
		}

		var all = form.querySelectorAll<HTMLInputElement>( '[data-gate-dob]' );
		var at = Array.prototype.indexOf.call( all, field );

		if ( at >= 0 && all[ at + 1 ] ) {
			all[ at + 1 ].focus();
		}
	} );

	document.addEventListener( 'keydown', function ( event: KeyboardEvent ) {
		var field = event.target;

		if ( 'Backspace' !== event.key || !( field instanceof HTMLInputElement ) ) {
			return;
		}

		if ( !field.matches( '[data-gate-dob]' ) || '' !== field.value ) {
			return;
		}

		var form = field.closest( '[data-gate-form]' );

		if ( !form ) {
			return;
		}

		var all = form.querySelectorAll<HTMLInputElement>( '[data-gate-dob]' );
		var at = Array.prototype.indexOf.call( all, field );

		if ( at > 0 ) {
			// Focus moves synchronously, so without this the browser's own delete lands on
			// the box we just moved to: one press would jump back AND eat a digit the
			// visitor cannot see yet. Moving back is the whole intent; the next press
			// deletes.
			event.preventDefault();
			all[ at - 1 ].focus();
		}
	} );

	// Delegated so it survives Slick cloning slides and sections revealed after load.
	document.addEventListener( 'click', function ( event: MouseEvent ) {
		var origin = event.target;

		if ( !( origin instanceof Element ) ) {
			return;
		}

		var slide = origin.closest<HTMLElement>( '.game-media__slide' );

		if ( !slide || slide.classList.contains( 'game-media__slide--external' ) ) {
			return;
		}

		// A covered section must not open anything, even if a slide is somehow clickable.
		if ( slide.closest( '.game-media--gated' ) ) {
			return;
		}

		if ( !slide.getAttribute( 'data-media-video' ) && !slide.getAttribute( 'data-media-full' ) ) {
			return;
		}

		var section = slide.closest<HTMLElement>( '.game-media' );

		if ( !section ) {
			return;
		}

		event.preventDefault();
		openLightbox( section, indexOf( section, slide ), slide );
	} );
})( jQuery );
